import { yupResolver } from '@hookform/resolvers/yup';
import Add from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Alert,
  FormControl,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import { getStrippedText } from '@utils/helper';
import { FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as yup from 'yup';

import { EditIcon } from '../../../vectors/index';
import { useCopyTemplate } from '../hooks/useCopyTemplate';
import { EnterpriseTemplate } from '../types';

type CopyTemplateModalProps = {
  item: EnterpriseTemplate;
  handleClose: () => void;
};

const schema = yup
  .object({
    templateName: yup
      .string()
      .required('Name is required')
      .test('not-empty-html', 'Name cannot be empty', (value) => {
        if (!value) return false;
        return getStrippedText(value).length > 0;
      }),
    templateDescription: yup
      .string()
      .required('Description is required')
      .test('not-empty-html', 'Description cannot be empty', (value) => {
        if (!value) return false;
        return getStrippedText(value).length > 0;
      }),
    // status: yup.number().required('Status is required'), // Optional validation
  })
  .required();

const CopyTemplateModal: FC<CopyTemplateModalProps> = ({
  handleClose,
  item,
}) => {
  const [errorText, setErrorText] = useState<string>('');
  const [billing, setBilling] = useState<string>('');
  const [applyChanges, setApplyChanges] = useState(false);
  const { copyTemplate, isCopingTemplate } = useCopyTemplate({ setErrorText });

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const handleAdd = (data: {
    templateName: string;
    templateDescription: string;
  }) => {
    const options = {
      onSuccess: (data: { message: string }) => {
        toast.success(data.message);
        handleClose();
      },
    };

    copyTemplate(
      {
        name: data.templateName,
        description: data.templateDescription,
        template_id: item.id,
        apply_changes: applyChanges,
        payment_method: Number(billing),
        // status: data.status
      },
      options,
    );
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    setApplyChanges(checked);
  };

  return (
    <Dialog open onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
        <br />
        <EditIcon />
        <br />
        <Typography variant="h4">
          Copy <b>{item.name}</b> Template
        </Typography>
      </DialogTitle>
      <DialogContent>
        {errorText && (
          <Alert sx={{ margin: '10px 0' }} severity="error">
            {errorText}
          </Alert>
        )}
        <form
          onSubmit={handleSubmit(handleAdd)}
          style={{ display: 'flex', flexDirection: 'column' }}
        >
          <FormControl sx={{ marginBottom: '10px' }} fullWidth>
            <Typography id="pay-rate-type-label"> Name</Typography>
            <Controller
              name="templateName"
              control={control}
              defaultValue={item.name}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  variant="outlined"
                  error={!!errors.templateName}
                  helperText={errors.templateName?.message}
                  fullWidth
                />
              )}
            />
          </FormControl>
          <FormControl sx={{ marginBottom: '10px' }} fullWidth>
            <Typography id="pay-rate-type-label"> Description</Typography>
            <Controller
              name="templateDescription"
              control={control}
              defaultValue={item.description}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  variant="outlined"
                  error={!!errors.templateDescription}
                  helperText={errors.templateDescription?.message}
                  fullWidth
                  multiline
                  rows={4}
                  maxRows={4}
                />
              )}
            />
          </FormControl>

          <FormControl component="fieldset">
            <RadioGroup
              aria-label="template-billing"
              name="payment_method"
              value={billing}
              onChange={(e) => setBilling(e.target.value)}
            >
              <FormControlLabel
                value="1"
                control={<Radio />}
                label="Bill to Enterprise"
              />
              <FormControlLabel
                value="0"
                control={<Radio />}
                label="Bill to Individual Employer"
              />
            </RadioGroup>
          </FormControl>

          <FormControl component="fieldset">
            <Stack
              direction="row"
              spacing={1}
              alignItems="start"
              sx={{ mt: 2, marginLeft: '-10px', mb: 2 }}
            >
              <Switch
                onChange={handleSwitchChange}
                size="medium"
                checked={applyChanges}
              />
              <Box mb={2}>
                <Typography sx={{ whiteSpace: 'nowrap' }}>
                  Global Edits
                </Typography>
                <Typography
                  color="gray"
                  sx={{ fontSize: '12px', whiteSpace: 'nowrap' }}
                >
                  Future edits to this template will {!applyChanges && 'not'}{' '}
                  affect {applyChanges && 'all'} companies attached.
                </Typography>
              </Box>
            </Stack>
          </FormControl>

          <LoadingButton
            loading={isCopingTemplate}
            disabled={isCopingTemplate}
            fullWidth
            startIcon={<Add />}
            variant="contained"
            color="primary"
            type="submit"
            sx={{ marginTop: 2, marginBottom: 2 }}
          >
            Save Template
          </LoadingButton>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CopyTemplateModal;
